import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  LazyMotion,
  domAnimation,
  m,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { blogUrl } from "../../lib/siteUrl";
import {
  type CustomerCategory,
  type CustomerLogoItem,
  getCustomerLogos,
} from "./customer-logos";

const FLIP_DURATION = 0.6;
const FLIP_HOLD_MS = 3000;
const FLIP_TICK_MS = 1800;
const HOVER_LEAVE_DELAY = 0.2;
const MAX_AUTO_FLIPPED = 3;

const headerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.04 },
  },
};

const headerChild = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

function CustomerLogoCell({
  item,
  flipped,
  onHoverChange,
}: {
  item: CustomerLogoItem;
  flipped: boolean;
  onHoverChange: (filename: string, hovering: boolean) => void;
}) {
  const href = item.blogSlug ? blogUrl(item.blogSlug) : undefined;
  const cursorClass = href ? "cursor-pointer" : "cursor-default";
  const cellClassName = `relative block h-[100px] border-b border-r border-default-100 bg-black [perspective:900px] ${cursorClass}`;

  const content = (
    <m.div
      className="pointer-events-none relative h-full w-full"
      style={{ transformStyle: "preserve-3d" }}
      initial={false}
      animate={{ rotateX: flipped ? 180 : 0 }}
      transition={{
        duration: FLIP_DURATION,
        // 翻回正面时延迟 0.3s 再开始动画
        delay: flipped ? 0 : HOVER_LEAVE_DELAY,
        ease: [0.45, 0, 0.55, 1],
      }}
    >
      {/* 正面：Logo */}
      <div
        className="absolute inset-0 flex items-center justify-center overflow-hidden bg-black"
        style={{
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
        }}
      >
        <img
          src={`/images/customer_logo/${encodeURIComponent(item.filename)}`}
          alt={item.name}
          className="relative z-[1] h-7 w-auto max-w-[72%] object-contain opacity-90 grayscale sm:h-8 md:h-9"
          draggable={false}
        />
      </div>

      {/* 背面：名称 + 品类 */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-[12px] overflow-hidden bg-[#141414] px-3"
        style={{
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          transform: "rotateX(180deg)",
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 100% 0%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 35%, transparent 65%)",
          }}
        />
        <span className="relative z-[1] text-center text-sm font-medium leading-none text-white sm:text-[16px]">
          {item.name}
        </span>
        <span className="relative z-[1] rounded-full border border-white/10 bg-white/10 px-2.5 py-0.5 text-[11px] leading-none text-white/70 backdrop-blur-sm sm:text-xs">
          {item.category}
        </span>
      </div>
    </m.div>
  );

  // 外层不参与 3D 变换，保证移入/移出命中稳定
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`查看${item.name}客户案例`}
        className={cellClassName}
        onMouseEnter={() => onHoverChange(item.filename, true)}
        onMouseLeave={() => onHoverChange(item.filename, false)}
      >
        {content}
      </a>
    );
  }

  return (
    <div
      className={cellClassName}
      onMouseEnter={() => onHoverChange(item.filename, true)}
      onMouseLeave={() => onHoverChange(item.filename, false)}
    >
      {content}
    </div>
  );
}

/** 与 grid-cols-2 / sm:grid-cols-3 / lg:grid-cols-4 对齐 */
const COLS_BY_BREAKPOINT = { base: 2, sm: 3, lg: 4 } as const;

function padCount(total: number, cols: number) {
  const remainder = total % cols;
  return remainder === 0 ? 0 : cols - remainder;
}

type EmptyCellVisibility = {
  base: boolean;
  sm: boolean;
  lg: boolean;
};

/** 方案页：按 2/3/4 列分别计算需补齐的空白格，并用响应式显隐对齐各断点 */
function getSolutionEmptyCells(logoCount: number): EmptyCellVisibility[] {
  const pad2 = padCount(logoCount, COLS_BY_BREAKPOINT.base);
  const pad3 = padCount(logoCount, COLS_BY_BREAKPOINT.sm);
  const pad4 = padCount(logoCount, COLS_BY_BREAKPOINT.lg);
  const maxPad = Math.max(pad2, pad3, pad4);

  return Array.from({ length: maxPad }, (_, i) => ({
    base: i < pad2,
    sm: i < pad3,
    lg: i < pad4,
  }));
}

function EmptyLogoCell({ visibility }: { visibility: EmptyCellVisibility }) {
  const visibilityClass = [
    visibility.base ? "block" : "hidden",
    visibility.sm ? "sm:block" : "sm:hidden",
    visibility.lg ? "lg:block" : "lg:hidden",
  ].join(" ");

  return (
    <div
      aria-hidden
      className={`relative h-[100px] cursor-default border-b border-r border-default-100 bg-black ${visibilityClass}`}
    />
  );
}

type BrandsSectionProps = {
  /** 按行业筛选；不传则展示首页全量 logo 墙 */
  category?: CustomerCategory;
};

export default function BrandsSection({ category }: BrandsSectionProps = {}) {
  const isSolutionPage = Boolean(category);
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { margin: "-12% 0px" });
  const reduceMotion = useReducedMotion();
  const logos = getCustomerLogos(category);
  const emptyCells = isSolutionPage ? getSolutionEmptyCells(logos.length) : [];

  const [autoFlipped, setAutoFlipped] = useState<Set<string>>(() => new Set());
  const [hovered, setHovered] = useState<string | null>(null);
  const autoFlippedRef = useRef(autoFlipped);
  const hoveredRef = useRef(hovered);
  const flipTimersRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    autoFlippedRef.current = autoFlipped;
  }, [autoFlipped]);

  useEffect(() => {
    hoveredRef.current = hovered;
  }, [hovered]);

  const clearFlipTimer = useCallback((filename: string) => {
    const timer = flipTimersRef.current.get(filename);
    if (timer != null) {
      window.clearTimeout(timer);
      flipTimersRef.current.delete(filename);
    }
  }, []);

  const scheduleFlipBack = useCallback(
    (filename: string) => {
      clearFlipTimer(filename);
      const timer = window.setTimeout(() => {
        setAutoFlipped((prev) => {
          if (!prev.has(filename)) return prev;
          const next = new Set(prev);
          next.delete(filename);
          return next;
        });
        flipTimersRef.current.delete(filename);
      }, FLIP_HOLD_MS);
      flipTimersRef.current.set(filename, timer);
    },
    [clearFlipTimer],
  );

  const handleHoverChange = useCallback(
    (filename: string, hovering: boolean) => {
      if (hovering) {
        setHovered(filename);
        clearFlipTimer(filename);
        setAutoFlipped((prev) => {
          if (!prev.has(filename)) return prev;
          const next = new Set(prev);
          next.delete(filename);
          return next;
        });
        return;
      }
      setHovered((current) => (current === filename ? null : current));
    },
    [clearFlipTimer],
  );

  useEffect(() => {
    // 解决方案页不做自动翻转
    if (isSolutionPage || reduceMotion || !isInView) return;

    const homepageLogos = getCustomerLogos();
    if (homepageLogos.length === 0) return;

    const tick = () => {
      const current = autoFlippedRef.current;
      if (current.size >= MAX_AUTO_FLIPPED) return;

      const candidates = homepageLogos
        .map((item) => item.filename)
        .filter((id) => !current.has(id) && id !== hoveredRef.current);

      if (candidates.length === 0) return;

      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      setAutoFlipped((prev) => {
        if (prev.has(pick) || prev.size >= MAX_AUTO_FLIPPED) return prev;
        const next = new Set(prev);
        next.add(pick);
        return next;
      });
      scheduleFlipBack(pick);
    };

    const intervalId = window.setInterval(tick, FLIP_TICK_MS);
    const startId = window.setTimeout(tick, 900);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(startId);
    };
  }, [reduceMotion, isInView, scheduleFlipBack, isSolutionPage]);

  useEffect(() => {
    return () => {
      flipTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      flipTimersRef.current.clear();
    };
  }, []);

  if (logos.length === 0) return null;

  return (
    <section
      ref={sectionRef}
      id={isSolutionPage ? undefined : "brands"}
      className={
        isSolutionPage
          ? "relative mx-auto w-full max-w-6xl px-6 pt-[80px] lg:px-8"
          : "relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28 lg:px-8 lg:py-36"
      }
    >
      <LazyMotion features={domAnimation}>
        {!isSolutionPage ? (
          <m.div
            className="relative mb-10 flex flex-col items-center text-center sm:mb-12"
            variants={headerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px -8% 0px" }}
          >
            <m.h2
              variants={headerChild}
              className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl sm:leading-[1.25] lg:text-[2rem]"
            >
              部分客户
            </m.h2>
          </m.div>
        ) : null}

        <m.div
          className="grid grid-cols-2 border-l border-t border-default-100 sm:grid-cols-3 lg:grid-cols-4"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8% 0px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          {logos.map((item) => {
            const flipped =
              hovered === item.filename || autoFlipped.has(item.filename);
            return (
              <CustomerLogoCell
                key={item.filename}
                item={item}
                flipped={flipped}
                onHoverChange={handleHoverChange}
              />
            );
          })}
          {emptyCells.map((visibility, index) => (
            <EmptyLogoCell key={`empty-${index}`} visibility={visibility} />
          ))}
        </m.div>
      </LazyMotion>
    </section>
  );
}
